<template>
  <v-dialog
    v-model="isOpen"
    width="1000"
  >
    <v-card>
      <v-card-title class="subtitle-1 justify-end pb-0">
        <v-icon @click="destroySetup">mdi-window-close</v-icon>
      </v-card-title>
      <v-card-title class="subtitle-1 text-uppercase justify-center pt-0">Activation Window</v-card-title>
      <v-card-text>
        <v-row class="activation-modal__name-container">
          <v-col md="4">
            <v-text-field
              name="audienceName"
              label="Audience Name"
              id="activationModalAudienceName"
              v-model="segment.display_name"
              readonly
              hide-details="true"
              dense
              filled
            ></v-text-field>
          </v-col>
          <v-col md="4">
            <advertiser-select
              @update="val => (segment.advertiser = val)"
              :initialValue="segment.advertiser"
              :showAdvertiserId="true"
              :disabled="disableAdvertiser"
              :filled="true"
            />
          </v-col>
          <v-col
            v-if="showViewingStream"
            md="4"
          >
            <streaming-select
              @input="val => (segment.viewing_stream = val)"
              :initialValue="segment.viewing_stream"
              :viewingOption="viewingOption"
              :disabled="disableViewingStream"
            />
          </v-col>
        </v-row>
        <div class="activation-modal__bottom-container">
          <div class="activation-modal__endpoints-area">
            <div
              class="activation-modal__destinations"
              v-for="destination in destinations"
              :key="destination.id"
            >
              <MVPD
                :destination="destination"
                :selected="segment.destinations.includes(destination.id)"
                :canUpdate="false"
                :UE="0"
                @selectedDestination="selectDestination(destination)"
              />
            </div>
          </div>
          <v-container
            v-if="!foresiteDestinationSelected"
            class="activation-modal__campaign-area"
          >
            <v-row
              v-if="isDigitalDestination || isDDLDestination"
              style="margin: 0px"
            >
              <flight-dates
                :value="{
                  flightStartDate: segment.am_data.flightStartDate,
                  flightEndDate: segment.am_data.flightEndDate,
                }"
                @input="val => Object.assign(segment.am_data, val)"
              />
            </v-row>
            <v-divider
              v-if="isFreeWheelOrGAMDestination && hasNotBeenPreviouslyModeled"
              class="mt-5 mb-5"
            ></v-divider>
            <v-row
              v-if="isFreeWheelOrGAMDestination && hasNotBeenPreviouslyModeled"
              style="margin: 0px"
            >
              <segment-split
                @valid="val => (isSplitValid = val)"
                @segmentSplit="val => (segmentSplit = val)"
              />
            </v-row>
            <v-row
              v-if="showFrequency"
              style="margin: 0px"
            >
              <frequency-d-d-l
                @input="val => (segment.xandr_data.frequency = val)"
                :initialValue="frequency"
                :optionSet="frequencyOptionSet"
                :disabled="disableFrequency"
              />
            </v-row>
          </v-container>
          <div
            v-if="foresiteDestinationSelected"
            class="activation-modal__broadcast-quarter-area"
          >
            <broadcast-quarter-select @input="val => (broadcastQuarter = val)" />
            <demo-qualifier @demoQualifier="val => (demoQualifier = val)" />
          </div>
        </div>
      </v-card-text>
      <v-card-text>
        <v-row
          v-if="showAdvertiserRequiredMessage"
          class="justify-space-around subtitle-1 font-weight-bold"
        >
          Advertiser is required in order to send downstream
        </v-row>
      </v-card-text>
      <v-card-actions
        class="pb-6"
        style="min-height: 60px"
      >
        <v-row class="justify-space-around">
          <action-button
            v-if="!activatingAudience && !disableSendDownstream"
            dark
            :disabled="disableSendDownstream"
            @click.native="submitDownstream"
            label="Send Downstream"
            color="primary"
            id="sendDownstreamModalBtn"
          />
        </v-row>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
  import ActionButton from '@/components/common/actionButton'
  import AdvertiserSelect from '@/appComponents/targets/components/AdvertiserSelect'
  import StreamingSelect from '@/appComponents/targets/components/StreamingSelect'
  import MVPD from '@/appComponents/targets/createTarget/components/MVPD.vue'
  import FlightDates from '@/appComponents/targets/createTarget/components/FlightDates'
  import FrequencyDDL from '@/appComponents/targets/components/FrequencyDDL'
  import BroadcastQuarterSelect from '@/appComponents/targets/components/BroadcastQuarterSelect'
  import DemoQualifier from '@/appComponents/targets/components/DemoQualifier'
  import SegmentSplit from '@/appComponents/targets/createTarget/components/SegmentSplit'
  import { convertViewingStreamForAM } from '@/appLogic/targets/conversions'
  import { getAllDatasetsRootFamilyFromQuery } from '@/common/U'

  import { cloneDeep, merge } from 'lodash'
  import {
    DESTINATIONS,
    LAS_DESTINATIONS,
    ADDRESSABLE_DESTINATIONS,
    DIGITAL_DESTINATIONS,
    DDL_DESTINATIONS,
    DESTINATION_OBJECTS,
    FREQUENCY_OPTION_SET,
    TARGET_TYPE,
    TARGET_TYPES_INTEGERS,
    FREQUENCIES,
    VIEWING_STREAMS,
    MARKETPLACE_ENDPOINTS,
    TARGET_ACTIONS,
    ACTIVATION_STATUS,
  } from '@/appLogic/targets/Constants'
  import {
    SUBMIT_DOWNSTREAM,
    SET_ENABLE_DOWN_STREAM,
    SET_TARGET,
    SAVE_TARGET,
  } from '@/appLogic/workbench/common/store/actions'
  import { POST_TARGET } from '@/views/audienceHub/store/actions'
  import { SET_UPDATING_TARGET, RESET_THE_LAS_MODAL } from '@/appLogic/targets/createTarget/store/actions'
  import { validationMixin } from 'vuelidate'
  import { required, minLength, requiredIf } from 'vuelidate/lib/validators'
  import { external } from 'jszip'

  export default {
    mixins: [validationMixin],
    components: {
      ActionButton,
      AdvertiserSelect,
      StreamingSelect,
      MVPD,
      FlightDates,
      FrequencyDDL,
      BroadcastQuarterSelect,
      DemoQualifier,
      SegmentSplit,
    },
    props: {
      segment: {
        default: () => {
          return {
            display_name: '',
            destinations: [],
            xandr_data: {
              frequency: FREQUENCIES.MONTHLY,
            },
            viewing_stream: null,
            am_data: {
              flightStartDate: null,
              flightEndDate: null,
              status: null,
              xdFlightOverlap: 0,
            },
          }
        },
        display_name: {
          type: String,
          required: true,
          default: '',
        },
        destinations: {
          type: Array,
          required: true,
          default: [],
        },
        advertiser: {
          type: Object,
          required: true,
          default: null,
        },
        viewing_stream: {
          type: String,
          required: true,
          default: null,
        },
        xandr_data: {
          frequency: {
            type: Number,
            default: null,
          },
        },
      },
      value: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        isOpen: this.isActivationDialogOpen,
        next: null,
        modalTitle: 'Activation Window',
        disableViewingStream: true,
        disableAdvertiser: true,
        destinations: [],
        MVPDs: [],
        frequency: null,
        frequencyOptionSet: this.selectedAdstraDatasetFamily ? FREQUENCY_OPTION_SET.MONTHLY : 0,
        isLoading: true,
        buttonClicked: false,
        broadcastQuarter: null,
        demoQualifier: null,
        foresiteDestinationSelected: false,
        isSplitValid: true,
        segmentSplit: [],
      }
    },
    validations() {
      return {
        segment: {
          display_name: { required },
          advertiser: {
            Name: {
              required: requiredIf(function () {
                return this.advertiserRequired
              }),
            },
          },
          viewing_stream: { required },
          destinations: { required, minLength: minLength(1) },
        },
      }
    },
    created() {
      if (this.activatingAudience) {
        this.segment.xandr_data = { frequency: null }
      }
      this.setDestinations()
    },
    mounted() {
      if (this.activatingAudience) {
        this.segment.xandr_data = { frequency: null }
      }
      this.isLoading = false
    },
    computed: {
      nielsenDatasources() {
        return this.$store.getters.getNielsenDatasources
      },
      C360Datasources() {
        return this.$store.getters.getC360Datasources
      },
      advertiserRequired() {
        return this.isAddressableDestination || this.isLASDestination
      },
      advertiser() {
        return `${this.segment.advertiser.Advertiser_id} ${this.segment.advertiser.Name}`
      },
      activatingAudience() {
        return this.$store.state.createTarget.initialTargetAction === TARGET_ACTIONS.ACTIVATE
      },
      showAdvertiserRequiredMessage() {
        if (!this.segment.destinations || this.segment.destinations.length === 0) return false
        return (
          !(this.segment.advertiser && this.segment.advertiser.Name) &&
          (this.isAddressableDestination || this.segment.destinations.includes(DESTINATIONS.FORESITE))
        )
      },
      disableFrequency() {
        return false
      },
      showViewingStream() {
        return (
          this.selectedLasDatasetFamily ||
          (ADDRESSABLE_DESTINATIONS.some(d => this.availableDestinations.includes) && this.isAddressableDestination)
        )
      },
      showFrequency() {
        return this.isAddressableDestination
      },
      selectedLasDatasetFamily() {
        let df = this.selectedDatasetFamily
        return df === 'amrld' || df === 'mri' || df === 'ncs'
      },
      selectedDatasetFamily() {
        return this.$store.getters['workbench/selectedFamilyDataset']
      },
      noSelectedDatasetFamily() {
        return !this.selectedDatasetFamily
      },
      selectedAdstraDatasetFamily() {
        return this.selectedDatasetFamily === TARGET_TYPE.ADSTRA || this.selectedDatasetFamily === 'xandr'
      },
      nielsenDatasource() {
        let rootFamilies = getAllDatasetsRootFamilyFromQuery(this.segment.target_definition.query)
        return rootFamilies.every(d =>
          [this.nielsenDatasources.AMRLD, this.nielsenDatasources.MRI, this.nielsenDatasources.NCS].includes(d),
        )
      },
      newWorkbenchTarget() {
        return this.$store.getters['workbench/newWorkbenchTarget']
      },
      disableSendDownstream() {
        return (
          (this.advertiserRequired && !(this.segment.advertiser && this.segment.advertiser.Name)) ||
          (this.foresiteDestinationSelected && !this.demoQualifier) ||
          this.$v.$invalid ||
          this.buttonClicked ||
          !this.$store.getters.hasPublishPermissions ||
          (this.isFreeWheelOrGAMDestination && !this.isSplitValid)
        )
      },
      availableDestinations() {
        return this.$store.getters['workbench/availableDestinations']
      },
      viewingOption() {
        return this.selectedAdstraDatasetFamily || this.isAddressableDestination ? 6 : 5
      },
      isAddressableDestination() {
        return this.segment.destinations && ADDRESSABLE_DESTINATIONS.some(a => this.segment.destinations.includes(a))
      },
      isDigitalDestination() {
        return this.segment.destinations && DIGITAL_DESTINATIONS.some(a => this.segment.destinations.includes(a))
      },
      isDDLDestination() {
        return this.segment.destinations && DDL_DESTINATIONS.some(a => this.segment.destinations.includes(a))
      },
      isLASDestination() {
        return this.segment.destinations && LAS_DESTINATIONS.some(a => this.segment.destinations.includes(a))
      },
      isFreeWheelOrGAMDestination() {
        return (
          this.segment.destinations &&
          this.segment.destinations.length > 0 &&
          this.segment.destinations.every(d => d === DESTINATIONS.FREEWHEEL || d === DESTINATIONS.GOOGLE_AD_MANAGER)
        )
      },
      hasNotBeenPreviouslyModeled() {
        return this.segment && this.segment.xandr_data && this.segment.xandr_data.isModeled !== true
      },
    },
    methods: {
      open() {
        this.buttonClicked = false
        this.getMVPDS()
        if (this.selectedLasDatasetFamily) {
          if (this.segment.destinations.length === 0) {
            this.segment.destinations = [DESTINATIONS.FORESITE]
          }
          this.segment.viewing_stream = convertViewingStreamForAM(this.$store.getters['workbench/viewingStream'])
        }
        this.setDestinations()
        if (this.destinations.length === 1) {
          this.selectDestination(this.destinations[0])
        }
      },
      close() {
        if (this.activatingAudience) {
          this.$store.dispatch(SET_UPDATING_TARGET, null)
        }
        this.isOpen = false
      },
      getMVPDS() {
        this.MVPDs = this.$store.state.appRes.mvpds
      },
      setDestinations(newVal = this.segment.destinations || []) {
        if (this.selectedLasDatasetFamily && this.segment.destinations && this.segment.destinations.length === 0) {
          this.segment.destinations = [DESTINATIONS.FORESITE]
          return
        }

        this.foresiteDestinationSelected = this.segment.destinations.includes(DESTINATIONS.FORESITE)

        this.destinations = DESTINATION_OBJECTS.filter(d => {
          if (this.availableDestinations.includes(d.id)) {
            return d.id
          }
        })
      },
      setExportFrequency() {
        if (this.isAddressableDestination) {
          this.frequency = FREQUENCIES.MONTHLY
          this.segment.xandr_data = { ...this.segment.xandr_data, frequency: FREQUENCIES.MONTHLY }
          delete this.segment.xandr_data.exportDayOfWeek
        } else if (
          this.segment.destinations.length > 0 &&
          this.segment.destinations.every(d => d === DESTINATIONS.VIDEOAMP)
        ) {
          this.segment.xandr_data = { ...this.segment.xandr_data, frequency: FREQUENCIES.ONE_TIME, exportDayOfWeek: 0 }
        } else if (this.isDigitalDestination) {
          this.segment.xandr_data = { ...this.segment.xandr_data, frequency: FREQUENCIES.WEEKLY, exportDayOfWeek: 0 }
        }
      },
      setXandrExternal() {
        if (!this.activatingAudience && (this.noSelectedDatasetFamily || this.selectedLasDatasetFamily)) return

        this.segment.xandr_data.external = undefined
        this.segment.xandr_data.addressable = undefined
        // Addressable
        if (this.isAddressableDestination) {
          if (!this.MVPDs) {
            this.getMVPDS()
          }
          this.setExportFrequency()
          this.segment.xandr_data.external = []
          this.segment.xandr_data.addressable = true
          this.segment.viewing_stream = VIEWING_STREAMS.LSD
          if (this.activatingAudience) {
            this.segment.target = TARGET_TYPE.ADSTRA
            this.segment.target_datasource = this.C360Datasources.ADSTRA
            this.segment.target_type = TARGET_TYPES_INTEGERS.ADSTRA
            this.segment.am_data = { flightStartDate: null, flightEndDate: null, status: null, xdFlightOverlap: 0 }
          }
          // Canoe Linear
          if (this.segment.destinations.includes(DESTINATIONS.CANOE_LINEAR)) {
            const canoe_mvpd = this.MVPDs.find(mvpd => mvpd.Name === MARKETPLACE_ENDPOINTS.CANOE_LINEAR)
            this.segment.xandr_data.external.push({
              endpointID: canoe_mvpd.Id,
              endpointName: canoe_mvpd.Name,
              externalID: '',
              universeEstimates: null,
            })
          }

          // Canoe VOD
          if (this.segment.destinations.includes(DESTINATIONS.CANOE_VOD)) {
            const canoe_vod_mvpd = this.MVPDs.find(mvpd => mvpd.Name === MARKETPLACE_ENDPOINTS.CANOE_VOD)
            this.segment.xandr_data.external.push({
              endpointID: canoe_vod_mvpd.Id,
              endpointName: canoe_vod_mvpd.Name,
              externalID: '',
              universeEstimates: null,
            })
          }

          // Vizio
          if (this.segment.destinations.includes(DESTINATIONS.VIZIO)) {
            const vizio_mvpd = this.MVPDs.find(mvpd => mvpd.Name === MARKETPLACE_ENDPOINTS.VIZIO)
            this.segment.xandr_data.external.push({
              endpointID: vizio_mvpd.Id,
              endpointName: vizio_mvpd.Name,
              externalID: '',
              universeEstimates: null,
            })

            this.segment.am_data.xdFlightOverlap = 0
          }

          // DirecTV
          if (this.segment.destinations.includes(DESTINATIONS.DIRECTV)) {
            const atttv_mvpd = this.MVPDs.find(mvpd => mvpd.Name === MARKETPLACE_ENDPOINTS.DIRECTV)
            this.segment.xandr_data.external.push({
              endpointID: atttv_mvpd.Id,
              endpointName: atttv_mvpd.Name,
              externalID: '',
              universeEstimates: null,
            })
          }
          // Non-Addressable
        } else {
          this.segment.xandr_data.addressable = false
          if (
            this.segment.destinations.find(destination =>
              [DESTINATIONS.FREEWHEEL, DESTINATIONS.GOOGLE_AD_MANAGER].includes(destination),
            )
          ) {
            if (this.activatingAudience) {
              this.segment.target = TARGET_TYPE.PRISM
              this.segment.target_datasource = this.C360Datasources.PRISM
              this.segment.target_type = TARGET_TYPES_INTEGERS.PRISM
              this.segment.am_data = { flightStartDate: null, flightEndDate: null, status: null, xdFlightOverlap: 0 }
            }
          } else if (this.segment.destinations.find(destination => [DESTINATIONS.FORESITE].includes(destination))) {
            if (this.segment.xandr_data.frequency === FREQUENCIES.WEEKLY) {
              this.frequency = null
            }
            this.frequencyOptionSet = process.env.ENABLE_FREQUENCIES ? FREQUENCY_OPTION_SET.LINEAR : 0
            if (this.activatingAudience) {
              this.segment.target = TARGET_TYPE.LAS
              this.segment.target_datasource = this.nielsenDatasources.MRI
              this.segment.target_type = TARGET_TYPES_INTEGERS.LAS
            }
          }
        }
      },
      selectDestination(destination) {
        const targetDestinations = new Set(this.segment.destinations)
        if (destination.isDisabled || (this.isActivated && !this.isDuplicating)) return
        const isDestinationAlreadySelected = targetDestinations.has(destination.id)
        const isAddressableDestinationFound = ADDRESSABLE_DESTINATIONS.some(a => targetDestinations.has(a))
        const isDigitalDestinationFound = DIGITAL_DESTINATIONS.some(a => targetDestinations.has(a))
        const isVideoAmpDestinationFound = targetDestinations.has(DESTINATIONS.VIDEOAMP)
        let that = this
        const clearSelectedDestinations = function () {
          that.segment.destinations = []
          targetDestinations.clear()
        }

        if (destination.id === DESTINATIONS.FORESITE && isDestinationAlreadySelected) {
          this.segment.viewing_stream = VIEWING_STREAMS.C3
          return
        }
        if (isDestinationAlreadySelected) {
          targetDestinations.delete(destination.id)
        } else {
          targetDestinations.add(destination.id)

          switch (destination.id) {
            case DESTINATIONS.FORESITE:
              if (isAddressableDestinationFound || isDigitalDestinationFound || isVideoAmpDestinationFound) {
                clearSelectedDestinations()
              }
              targetDestinations.add(destination.id)
              this.segment.viewing_stream = VIEWING_STREAMS.C3
              break
            case DESTINATIONS.DIRECTV:
            case DESTINATIONS.VIZIO:
            case DESTINATIONS.CANOE_LINEAR:
            case DESTINATIONS.CANOE_VOD:
              if (!isAddressableDestinationFound) {
                clearSelectedDestinations()
              }
              targetDestinations.add(destination.id)
              this.segment.viewing_stream = VIEWING_STREAMS.LSD
              break
            case DESTINATIONS.FREEWHEEL:
            case DESTINATIONS.GOOGLE_AD_MANAGER:
              if (!isDigitalDestinationFound && !isVideoAmpDestinationFound) {
                clearSelectedDestinations()
              }
              targetDestinations.add(destination.id)
              this.segment.viewing_stream = VIEWING_STREAMS.LSD
              break
            case DESTINATIONS.VIDEOAMP:
              if (!isDigitalDestinationFound) {
                clearSelectedDestinations()
              }
              targetDestinations.add(destination.id)
              this.segment.viewing_stream = VIEWING_STREAMS.LSD
              break
          }
        }
        this.segment.destinations = Array.from(targetDestinations)

        this.setXandrExternal()
      },
      setSegmentSplit() {
        if (this.isFreeWheelOrGAMDestination && this.segmentSplit.length > 0) {
          let segment = {
            segment: {
              segmentSplit: this.segmentSplit.map(split => {
                return { name: split.name, split: split.percentage / 100, externalId: this.segment.cae_id }
              }),
            },
          }
          this.segment.xandr_data = merge(this.segment.xandr_data, segment)
        }
      },
      submitDownstream() {
        this.buttonClicked = true
        if (this.foresiteDestinationSelected) {
          this.segment.target_definition = {
            ...this.segment.target_definition,
            broadcastQuarter: this.broadcastQuarter,
          }
          this.segment.metadata.foresite = { demoQualifier: this.demoQualifier }
        } else {
          delete this.segment.target_definition.broadcastQuarter
          if (this.segment.metadata.foresite) delete this.segment.metadata.foresite
        }
        this.setExportFrequency()
        this.setSegmentSplit()
        this.$store
          .dispatch('workbench/' + SAVE_TARGET, { target: cloneDeep(this.segment), ignoreSavePrompt: true })
          .then(resp => {
            if (this.nielsenDatasource) {
              let copiedTarget = cloneDeep(this.newWorkbenchTarget)
              this.$store.dispatch('workbench/' + SET_ENABLE_DOWN_STREAM, false)
              this.$store.dispatch(POST_TARGET, copiedTarget).then(resp => {
                this.$store.dispatch('workbench/' + SET_TARGET, copiedTarget)
                this.$router.push({ name: 'NewWorkbench', params: { id: this.segment.id } })
              })
            } else {
              this.$store.dispatch('workbench/' + SUBMIT_DOWNSTREAM)
            }
            this.close()
          })
      },
      destroySetup() {
        if (
          this.segment.activation_status === ACTIVATION_STATUS.DRAFT ||
          this.segment.activation_status === ACTIVATION_STATUS.WORKING
        ) {
          this.segment.destinations = []
        }
        if (this.activatingAudience) {
          this.segment.destinations = []
          if (this.segment.xandr_data) {
            delete this.segment.xandr_data.frequency
            delete this.segment.xandr_data.exportDayOfWeek
          }
          this.$store.dispatch(RESET_THE_LAS_MODAL)
        }
        this.close()
      },
    },
    watch: {
      value(val) {
        this.isOpen = val
      },
      isOpen(newVal) {
        if (newVal) {
          this.open()
        } else {
          this.close()
        }
        this.$emit('input', newVal)
      },
    },
  }
</script>

<style lang="scss">
  .mover-1 {
    animation: moveSlideshow 5s linear infinite;
  }

  @keyframes moveSlideshow {
    100% {
      transform: translateX(-66.6666%);
    }
  }

  .activation-modal {
    &__name-container {
      display: flex;
      justify-content: space-around;
      padding: 20px;
    }

    &__bottom-container {
      display: flex;
      padding: 20px;
    }

    &__endpoints-area {
      width: 60%;
      padding: 20px;
      display: flex;
      flex-wrap: wrap;
    }

    &__campaign-area {
      width: 40% !important;
      padding: 20px !important;
    }

    &__broadcast-quarter-area {
      width: 40%;
      padding: 30px;
    }

    &__full-width {
      width: 100%;
    }

    &__destinations {
      display: inline-block;
      margin-top: 15px;
    }

    &__advertiser-required {
      text-align: center;
      font-weight: bold;
      margin-bottom: 12px;
    }
  }
</style>



// WEBPACK FOOTER //
// ActivationModal.vue?10eedd7a